import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingDown,
  TrendingUp,
  MapPin,
  ArrowUpRight,
  ShieldCheck,
  Percent,
} from "lucide-react";
import InteractiveUSMap from "./InteractiveUSMap";
import {
  STATES,
  NATIONAL_AVG,
  STATE_LOAN_APR,
  NATIONAL_AVG_APR,
  findStateApr,
} from "../lib/states";
import { useGeo } from "../lib/geo";

/**
 * Iter-185.D / 185.G — State Savings Heatmap (homepage tabbed wrapper
 * around InteractiveUSMap).
 *
 * Iter-185.G additions: 2-tab toggle that lets the visitor swap between
 * an INSURANCE view (state-by-state full-coverage premium dispersion,
 * the iter-185.D default) and an AUTO-LOAN view (state-by-state 60mo
 * 720+ FICO APR dispersion). All three editorial surfaces flip with
 * the toggle:
 *   - Map shading (mode="insurance" vs mode="loans" on InteractiveUSMap).
 *   - 3-tile stat strip (cheapest / national / most expensive metric).
 *   - Geo-aware "your state" callout (correct deep-link to either
 *     /insurance/{state} or /auto-loans/{state}).
 *   - "View all 50 states" CTA destination.
 *
 * Truthfulness contract preserved:
 *   - All values still live-derived from STATES / STATE_LOAN_APR /
 *     NATIONAL_AVG / NATIONAL_AVG_APR (cannot drift).
 *   - Geo callout still gated on real ZIP→state resolution.
 *   - No invented numbers.
 *
 * Test IDs:
 *   - `state-savings-heatmap` (section root)
 *   - `state-savings-heatmap-tablist`
 *   - `state-savings-heatmap-tab-insurance` / `-auto-loan`
 *   - `state-savings-heatmap-stat-strip`
 *   - `state-savings-heatmap-cheapest`
 *   - `state-savings-heatmap-national-avg`
 *   - `state-savings-heatmap-most-expensive`
 *   - `state-savings-heatmap-detected`
 *   - `state-savings-heatmap-detected-cta`
 *   - `state-savings-heatmap-see-all`
 *   - `state-savings-heatmap-map-wrap`
 *   - `state-savings-heatmap-source`
 */
const MODE_TABS = [
  {
    id: "insurance",
    label: "Auto Insurance",
    Icon: ShieldCheck,
  },
  {
    id: "auto-loan",
    label: "Auto Loans",
    Icon: Percent,
  },
];

export default function StateSavingsHeatmap() {
  const { geo } = useGeo();
  const [mode, setMode] = useState("insurance");

  // Iter-185.G — Compute the mode-specific dataset + helpers in one
  // memoized pass. The shape unifies the rendering so the JSX below
  // doesn't need mode-conditional branches per cell.
  const view = useMemo(() => {
    const validInsurance = STATES.filter((s) => Number.isFinite(s.avg));
    const detectedCode = (geo && geo.state_code) || null;

    if (mode === "auto-loan") {
      // Build list of {code, name, slug, value} from the loan APR map.
      const rows = STATES.filter((s) => Number.isFinite(STATE_LOAN_APR[s.code]))
        .map((s) => ({
          code: s.code,
          name: s.name,
          slug: s.slug,
          value: STATE_LOAN_APR[s.code],
        }))
        .sort((a, b) => a.value - b.value);
      const detected = detectedCode
        ? rows.find((r) => r.code === detectedCode)
        : null;
      return {
        unit: "APR",
        eyebrow: "State APR pricing · 50 states + D.C.",
        h2: "See how much your state pays in auto-loan APR.",
        intro:
          "National lenders quote you off FICO + LTV — but state-level credit-union density, dealer-finance market share, and consumer-credit law shift the effective best APR by 0.5–2 percentage points across state lines. We track the 60-month, 720+ FICO average in all 50 states + D.C.",
        cheapest: rows[0],
        mostExpensive: rows[rows.length - 1],
        nationalAvg: NATIONAL_AVG_APR,
        detected,
        format: (v) => `${v.toFixed(1)}%`,
        diffFormat: (delta) => `${Math.abs(delta).toFixed(1)} pts`,
        mapMode: "loans",
        seeAllHref: "/auto-loans/states",
        seeAllLabel: "View all 50 state APRs",
        detectedHref: detected ? `/auto-loans/${detected.slug}` : null,
        cheapestHref: rows[0] ? `/auto-loans/${rows[0].slug}` : null,
        nationalHref: "/auto-loans",
        mostExpensiveHref: rows[rows.length - 1]
          ? `/auto-loans/${rows[rows.length - 1].slug}`
          : null,
        sourceCaption:
          "State APR averages reflect 60-month new-vehicle pricing for 720+ FICO borrowers in Q4 2025 (Experian State of the Auto Finance Market). Cross-referenced against NADA + Federal Reserve G.19 consumer-credit aggregates. Switch your ZIP above to localize lender APRs to your state.",
      };
    }

    // Default: insurance view.
    const sortedAsc = [...validInsurance].sort((a, b) => a.avg - b.avg);
    const detected = detectedCode
      ? validInsurance.find((s) => s.code === detectedCode)
      : null;
    return {
      unit: "premium",
      eyebrow: "State pricing · 50 states + D.C.",
      h2: "See how much your state pays for car insurance.",
      intro:
        "Crossing a state line can shift your full-coverage premium by $1,000+ per year — even with the same carrier and the same driving profile. We track the average in all 50 states + D.C. so you can spot the gap before you renew.",
      cheapest: sortedAsc[0]
        ? {
            code: sortedAsc[0].code,
            name: sortedAsc[0].name,
            slug: sortedAsc[0].slug,
            value: sortedAsc[0].avg,
          }
        : null,
      mostExpensive: sortedAsc[sortedAsc.length - 1]
        ? {
            code: sortedAsc[sortedAsc.length - 1].code,
            name: sortedAsc[sortedAsc.length - 1].name,
            slug: sortedAsc[sortedAsc.length - 1].slug,
            value: sortedAsc[sortedAsc.length - 1].avg,
          }
        : null,
      nationalAvg: NATIONAL_AVG,
      detected: detected
        ? { ...detected, value: detected.avg }
        : null,
      format: (v) => `$${v.toLocaleString()}`,
      diffFormat: (delta) => `$${Math.abs(delta).toLocaleString()}`,
      mapMode: "insurance",
      seeAllHref: "/auto-insurance/states",
      seeAllLabel: "View all 50 states + D.C.",
      detectedHref: detected ? `/insurance/${detected.slug}` : null,
      cheapestHref: sortedAsc[0] ? `/insurance/${sortedAsc[0].slug}` : null,
      nationalHref: "/auto-insurance",
      mostExpensiveHref: sortedAsc[sortedAsc.length - 1]
        ? `/insurance/${sortedAsc[sortedAsc.length - 1].slug}`
        : null,
      sourceCaption:
        "State averages reflect 2024 full-coverage premiums published by the National Association of Insurance Commissioners (NAIC) and state Department of Insurance filings. We update this dataset quarterly. Switch your ZIP above to localize the comparison tables to your state.",
    };
  }, [geo, mode]);

  const detectedDelta = view.detected
    ? view.detected.value - view.nationalAvg
    : 0;
  const detectedAbove = detectedDelta > 0;
  // Suffix per mode: "/yr" for insurance, "" for loans (% already in value).
  const valueSuffix = mode === "insurance" ? "/yr" : "";
  const trailerSuffix = mode === "insurance" ? "/yr" : "";

  return (
    <section
      data-testid="state-savings-heatmap"
      data-active-mode={mode}
      aria-label="State pricing heatmap"
      className="bg-[#FAFAFA] py-16 lg:py-24 border-t border-slate-200"
    >
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        {/* Editorial header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#10B981] mb-4">
              {view.eyebrow}
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-3xl">
              {view.h2}
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {view.intro}
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to={view.seeAllHref}
              data-testid="state-savings-heatmap-see-all"
              className="inline-flex items-center gap-2 font-bold text-slate-950 border-b border-slate-950 pb-1 hover:gap-3 transition-all"
            >
              {view.seeAllLabel}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Iter-185.G — Mode toggle (Insurance / Auto Loan).
            Matches the iter-185.B HomepageComparisonShelf tab vocabulary
            so the homepage reads consistently. */}
        <div
          role="tablist"
          aria-label="Heatmap vertical"
          data-testid="state-savings-heatmap-tablist"
          className="inline-flex bg-white border border-slate-200 p-1 mb-8"
        >
          {MODE_TABS.map((t) => {
            const Icon = t.Icon;
            const isActive = mode === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setMode(t.id)}
                data-testid={`state-savings-heatmap-tab-${t.id}`}
                data-active={isActive ? "true" : "false"}
                className={`min-h-[44px] inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-slate-950 text-white"
                    : "bg-transparent text-slate-600 hover:text-slate-950"
                }`}
              >
                <Icon
                  className={`w-4 h-4 ${
                    isActive ? "text-[#10B981]" : "text-slate-500"
                  }`}
                  strokeWidth={2}
                />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Geo-aware "your state" callout — only renders when ZIP →
            state resolves. Truthfulness gate. */}
        {view.detected && (
          <div
            data-testid="state-savings-heatmap-detected"
            className="bg-white border-l-2 border-[#10B981] px-5 py-4 mb-6 flex items-start gap-3"
          >
            <MapPin className="w-4 h-4 text-[#10B981] mt-1 shrink-0" />
            <div className="text-sm leading-relaxed text-slate-700">
              <span className="font-bold text-slate-950">
                Your state ({view.detected.name})
              </span>{" "}
              {mode === "insurance" ? "averages" : "sees"}{" "}
              <span className="font-display font-black text-slate-950 tabular-nums">
                {view.format(view.detected.value)}{valueSuffix}
              </span>{" "}
              {mode === "insurance" ? "for full coverage" : "on 60-month auto loans (720+ FICO)"} —{" "}
              <strong className={detectedAbove ? "text-rose-700" : "text-emerald-700"}>
                {view.diffFormat(detectedDelta)}{trailerSuffix} {detectedAbove ? "above" : "below"}
              </strong>{" "}
              the national average of {view.format(view.nationalAvg)}{valueSuffix}.{" "}
              <Link
                to={view.detectedHref}
                data-testid="state-savings-heatmap-detected-cta"
                className="text-slate-950 underline underline-offset-2 hover:text-[#10B981]"
              >
                See {view.detected.name} {mode === "insurance" ? "carriers" : "lenders"} →
              </Link>
            </div>
          </div>
        )}

        {/* 3-tile stat strip — Cheapest / National / Most expensive.
            All values derived live. */}
        <div
          data-testid="state-savings-heatmap-stat-strip"
          className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4 mb-8"
        >
          {view.cheapest && (
            <StatTile
              testId="state-savings-heatmap-cheapest"
              Icon={TrendingDown}
              tone="emerald"
              label={mode === "insurance" ? "Cheapest state" : "Lowest avg APR"}
              valueLabel={view.cheapest.name}
              value={view.format(view.cheapest.value)}
              suffix={valueSuffix}
              href={view.cheapestHref}
            />
          )}
          <StatTile
            testId="state-savings-heatmap-national-avg"
            Icon={MapPin}
            tone="slate"
            label="National average"
            valueLabel="50 states + D.C."
            value={view.format(view.nationalAvg)}
            suffix={valueSuffix}
            href={view.nationalHref}
          />
          {view.mostExpensive && (
            <StatTile
              testId="state-savings-heatmap-most-expensive"
              Icon={TrendingUp}
              tone="rose"
              label={mode === "insurance" ? "Most expensive state" : "Highest avg APR"}
              valueLabel={view.mostExpensive.name}
              value={view.format(view.mostExpensive.value)}
              suffix={valueSuffix}
              href={view.mostExpensiveHref}
            />
          )}
        </div>

        {/* Embedded interactive map. Iter-185.G — Map mode toggles with
            the active tab so the SVG shading + tooltips reflect the
            active metric. The iter-147 InteractiveUSMap component
            already accepts the `mode` prop. */}
        <div data-testid="state-savings-heatmap-map-wrap">
          <InteractiveUSMap mode={view.mapMode} />
        </div>

        {/* Editorial source caption */}
        <p
          data-testid="state-savings-heatmap-source"
          className="mt-6 text-[11px] text-slate-500 leading-relaxed max-w-3xl"
        >
          {view.sourceCaption}
        </p>
      </div>
    </section>
  );
}

function StatTile({ testId, Icon, tone, label, valueLabel, value, suffix = "", href }) {
  const toneClasses = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    slate: "border-slate-200 bg-white",
    rose: "border-rose-200 bg-rose-50/40",
  };
  const iconColor = {
    emerald: "text-emerald-600",
    slate: "text-slate-500",
    rose: "text-rose-600",
  };

  const inner = (
    <div
      data-testid={testId}
      className={`relative border px-5 py-5 transition-all ${toneClasses[tone]} ${
        href ? "hover:-translate-y-0.5 hover:shadow-md cursor-pointer" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`w-4 h-4 shrink-0 mt-1 ${iconColor[tone]}`}
          strokeWidth={2.25}
        />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            {label}
          </p>
          <div className="font-display font-black text-2xl lg:text-3xl text-slate-950 leading-none tracking-tight tabular-nums mb-1.5">
            {value}
            {suffix && (
              <span className="text-sm text-slate-500 font-bold ml-1">{suffix}</span>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-700 truncate">
            {valueLabel}
          </p>
        </div>
        {href && (
          <ArrowUpRight
            className="w-4 h-4 text-slate-500 shrink-0 mt-1"
            strokeWidth={2}
          />
        )}
      </div>
    </div>
  );

  return href ? <Link to={href}>{inner}</Link> : inner;
}
